import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { syncRefFromUrl } from "@/lib/refTracker";

import FunnelPage from "@/pages/Funnel";
import PartenzaPage from "@/pages/Partenza";
import AppEntry from "@/pages/AppEntry";
import OrizzontiPage from "@/pages/Orizzonti";
import OrizzontiCallPage from "@/pages/OrizzontiCall";
import LandingPage from "@/pages/LandingPage";
import Percorso from "@/pages/Percorso";
import {
  MagazineIndex,
  MagazineArticle,
  MagazineAlias,
} from "@/pages/ValoreImmobiliare";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import PolicyPropertyAdvisor from "@/pages/PolicyPropertyAdvisor";
import PropertyAdvisorLanding from "@/pages/PropertyAdvisorLanding";
import PercheVG from "@/pages/PercheVG";
import NetworkApplicationLanding from "@/pages/NetworkApplicationLanding";
import OwnerLanding from "@/pages/OwnerLanding";
import CapisciEntry from "@/pages/vg2/CapisciEntry";
import ValutaRapida from "@/pages/vg2/ValutaRapida";
import LegacyJourneyRedirect from "@/pages/vg2/LegacyJourneyRedirect";
import ValutaResult from "@/pages/vg2/ValutaResult";
import ValutaScenarioDeepDive from "@/pages/vg2/ValutaScenarioDeepDive";
import Decidi from "@/pages/vg2/Decidi";
import Agisci from "@/pages/vg2/Agisci";
import Analizza from "@/pages/vg2/Analizza";
import Eva from "@/pages/vg2/Eva";
import Workspace from "@/pages/vg2/Workspace";
import WorkspaceCase from "@/pages/vg2/WorkspaceCase";
import PaWorkspace from "@/pages/vg2/PaWorkspace";
import PaWorkspaceCase from "@/pages/vg2/PaWorkspaceCase";
import CookieBanner from "@/components/landing/CookieBanner";
import ScrollToTop from "@/components/landing/ScrollToTop";
import {
  LoginPage, SignupPage, ForgotPasswordPage, ResetPasswordPage, SignupSuccessReturn,
} from "@/pages/Auth";
import { OwnerSignupPage, OwnerLoginPage } from "@/pages/OwnerAuth";
import OwnerDashboard from "@/pages/OwnerDashboard";
import OwnerCaseDetail from "@/pages/OwnerCaseDetail";
import CodiceCondottaPage from "@/pages/CodiceCondotta";

import AgentLayout from "@/pages/dashboard/AgentLayout";
import AgentOverview from "@/pages/dashboard/Overview";
import AgentLeads from "@/pages/dashboard/Leads";
import AgentNetwork from "@/pages/dashboard/Network";
import AgentLink from "@/pages/dashboard/MyLink";
import AgentSettings from "@/pages/dashboard/Settings";
import SocialCoach from "@/pages/dashboard/SocialCoach";
import StrategyCoach from "@/pages/dashboard/StrategyCoach";
import Sara from "@/pages/dashboard/Sara";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminPromoCodes from "@/pages/admin/AdminPromoCodes";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminNetworkApplications from "@/pages/admin/AdminNetworkApplications";
import AdminPaRequests from "@/pages/admin/AdminPaRequests";
import AdminMarketing from "@/pages/admin/AdminMarketing";
import AdminPaAiUsage from "@/pages/admin/AdminPaAiUsage";
import MarketingUnsubscribe from "@/pages/MarketingUnsubscribe";

function PrivateRoute({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-gray-400">Caricamento…</div>;
  if (!user) {
    // Route owner-scoped areas back to the owner login, everyone else
    // to the legacy agent login.
    const dest = role === "owner" ? "/proprietari/login" : "/login";
    return <Navigate to={dest} replace />;
  }
  if (role && user.ruolo !== role) {
    // Send the user to their own home instead of the generic agent one.
    const own = user.ruolo === "owner" ? "/proprietari/dashboard"
      : user.ruolo === "admin" ? "/admin"
      : "/agent";
    return <Navigate to={own} replace />;
  }
  return children;
}

function AdaptiveDashboardLayout() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.ruolo === "owner") return <Navigate to="/proprietari/dashboard" replace />;
  if (user.ruolo === "admin") return <AdminLayout />;
  return <AgentLayout />;
}

function InfoPage({ children }) {
  return (
    <>
      <ScrollToTop />
      {children}
      <CookieBanner />
    </>
  );
}

// Cattura ?ref prima di qualsiasi redirect dei figli (anche rotte sconosciute).
syncRefFromUrl();

// Link referral storici /app?ref=CODICE → Home V3 con gli stessi parametri; /app senza ref resta legacy.
function AppRoute() {
  const { search } = useLocation();
  if (new URLSearchParams(search).get("ref")) return <Navigate to={`/${search}`} replace />;
  return <AppEntry />;
}

export default function App() {
  useEffect(() => {
    // Capture ?ref into cookie at app boot (covers landing on any route).
    syncRefFromUrl();
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/" element={<OwnerLanding />} />
          <Route path="/app" element={<AppRoute />} />
          <Route path="/valuta" element={<FunnelPage />} />
          <Route path="/partenza" element={<PartenzaPage />} />
          <Route path="/orizzonti" element={<OrizzontiPage />} />
          <Route path="/orizzonti/call" element={<OrizzontiCallPage />} />
          <Route path="/scopri" element={<InfoPage><LandingPage /></InfoPage>} />
          <Route path="/percorso" element={<InfoPage><Percorso /></InfoPage>} />
          <Route path="/valore-immobiliare" element={<InfoPage><MagazineIndex /></InfoPage>} />
          <Route path="/valore-immobiliare/:slug" element={<InfoPage><MagazineArticle /></InfoPage>} />
          <Route path="/magazine" element={<MagazineAlias />} />
          <Route path="/magazine/*" element={<MagazineAlias />} />
          <Route path="/privacy" element={<InfoPage><PrivacyPolicy /></InfoPage>} />
          <Route path="/cookie" element={<InfoPage><CookiePolicy /></InfoPage>} />
          <Route path="/policy" element={<InfoPage><PolicyPropertyAdvisor /></InfoPage>} />
          <Route path="/professionisti" element={<NetworkApplicationLanding />} />
          <Route path="/property-advisor" element={<PropertyAdvisorLanding />} />
          <Route path="/property-advisor/signup" element={<SignupPage />} />
          <Route path="/property-advisor/login" element={<LoginPage />} />
          <Route path="/perche-vg" element={<PercheVG />} />
          <Route path="/proprietari" element={<OwnerLanding />} />

          {/* --- VG 2.0 Phase 3A — isolated preview journey --- */}
          <Route path="/vg2" element={<CapisciEntry />} />
          <Route path="/vg2/:journeyId" element={<ValutaRapida />} />
          <Route path="/vg2/capisci/:journeyId" element={<LegacyJourneyRedirect />} />
          <Route path="/vg2/capisci/:journeyId/risultato" element={<LegacyJourneyRedirect />} />
          <Route path="/vg2/valuta/:journeyId/transizione" element={<LegacyJourneyRedirect />} />
          <Route path="/vg2/valuta/:journeyId" element={<LegacyJourneyRedirect />} />
          <Route path="/vg2/valuta/:journeyId/risultato/:analysisId" element={<ValutaResult />} />
          <Route path="/vg2/valuta/scenario/:caseId/:analysisId/:scenarioKey" element={<ValutaScenarioDeepDive />} />
          <Route path="/vg2/decidi/:caseId" element={<Decidi />} />
          <Route path="/vg2/agisci/:caseId" element={<Agisci />} />
          <Route path="/vg2/analizza/:caseId/:scenarioKey" element={<Analizza />} />
          <Route path="/vg2/eva/:caseId" element={<Eva />} />
          <Route path="/vg2/workspace" element={<Workspace />} />
          <Route path="/vg2/workspace/cases/:caseId" element={<WorkspaceCase />} />
          <Route path="/vg2/pa" element={<PaWorkspace />} />
          <Route path="/vg2/pa/cases/:caseId" element={<PaWorkspaceCase />} />

          <Route path="/login" element={<LoginPage />} />
          <Route path="/marketing/disiscrizione" element={<MarketingUnsubscribe />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/signup/success" element={<SignupSuccessReturn />} />
          <Route path="/signup/cancel" element={<Navigate to="/signup" replace />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* M8 · public code-of-conduct static page (linked from PA signup) */}
          <Route path="/codice-di-condotta" element={<CodiceCondottaPage />} />

          {/* OwnerDesk V1 · owner-scoped auth + dashboard */}
          <Route path="/proprietari/login" element={<OwnerLoginPage />} />
          <Route path="/proprietari/signup" element={<OwnerSignupPage />} />
          <Route path="/proprietari/dashboard"
                 element={<PrivateRoute role="owner"><OwnerDashboard /></PrivateRoute>} />
          <Route path="/proprietari/casi/:caseId"
                 element={<PrivateRoute role="owner"><OwnerCaseDetail /></PrivateRoute>} />

          <Route path="/agent" element={<PrivateRoute><AdaptiveDashboardLayout /></PrivateRoute>}>
            <Route index element={<AgentOverview />} />
            <Route path="leads" element={<AgentLeads />} />
            <Route path="network" element={<AgentNetwork />} />
            <Route path="link" element={<AgentLink />} />
            <Route path="social-coach" element={<SocialCoach />} />
            <Route path="strategy-coach" element={<StrategyCoach />} />
            <Route path="sara" element={<Sara />} />
            <Route path="settings" element={<AgentSettings />} />
          </Route>

          <Route path="/admin" element={<PrivateRoute role="admin"><AdminLayout /></PrivateRoute>}>
            <Route index element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="promo-codes" element={<AdminPromoCodes />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="network-applications" element={<AdminNetworkApplications />} />
            <Route path="pa-requests" element={<AdminPaRequests />} />
            <Route path="marketing" element={<AdminMarketing />} />
            <Route path="pa-ai-usage" element={<AdminPaAiUsage />} />
          </Route>

          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
